import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds';
import type { Dict, Mixpanel } from 'mixpanel-browser';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ButtonName, PageName } from '@/libs/mixpanel';

type StaticProperties = { app_version: string };
type DynamicProperties = { language?: string };
export type MixpanelProperties = Dict & DynamicProperties;

interface MixpanelState {
  lastPageViewTracked: { date: Date; pageName: string } | undefined;
  mixpanel: Mixpanel | null;
  setMixpanel: (mixpanel: Mixpanel | null, staticProperties: StaticProperties) => void;
  staticProperties: StaticProperties | undefined;
  trackButtonClicked: (buttonName: ButtonName, properties?: MixpanelProperties) => void;
  trackPageView: (pageName: PageName, properties?: MixpanelProperties) => void;
  trackAction: (actionName: string, properties?: MixpanelProperties) => void;
  identify: (userId: string, userProperties?: Record<string, unknown>) => void;
  setUserProperties: (userProperties: Record<string, unknown>) => void;
  reset: () => void;
}

export const useMixpanel = create<MixpanelState>()(
  devtools((set, get) => ({
    lastPageViewTracked: undefined,
    mixpanel: null,
    setMixpanel: (mixpanel, staticProperties) => {
      set(() => ({ mixpanel, staticProperties }));
    },
    staticProperties: undefined,
    trackButtonClicked: (buttonName: ButtonName, properties?: MixpanelProperties) => {
      const { mixpanel, staticProperties } = get();

      if (!mixpanel) return;

      mixpanel.track('button_clicked', {
        button_name: buttonName,
        ...staticProperties,
        ...properties,
      });
    },
    trackPageView: (pageName: PageName, properties?: MixpanelProperties) => {
      const { lastPageViewTracked, mixpanel, staticProperties } = get();

      if (!mixpanel) return;

      if (lastPageViewTracked?.pageName === pageName) {
        const diff = differenceInMilliseconds(new Date(), lastPageViewTracked.date);
        if (diff <= 500) return;
      }

      set(() => ({ lastPageViewTracked: { date: new Date(), pageName } }));

      mixpanel.track('page_viewed', {
        page_name: pageName,
        ...staticProperties,
        ...properties,
      });
    },
    trackAction: (actionName: string, properties?: MixpanelProperties) => {
      const { mixpanel, staticProperties } = get();

      if (!mixpanel) return;

      mixpanel.track(actionName, {
        ...staticProperties,
        ...properties,
      });
    },
    identify: (userId: string, userProperties?: Record<string, unknown>) => {
      const { mixpanel } = get();

      if (mixpanel) {
        mixpanel.identify(userId);
        if (userProperties) {
          mixpanel.people.set(userProperties);
        }
      }
    },
    setUserProperties: (userProperties: Record<string, unknown>) => {
      const { mixpanel } = get();

      if (!mixpanel) return;

      mixpanel.people.set(userProperties);
    },
    reset: () => {
      const { mixpanel } = get();

      if (mixpanel) {
        mixpanel.reset();
      }
      set(() => ({
        lastPageViewTracked: undefined,
        mixpanel: undefined,
        staticProperties: undefined,
      }));
    },
  })),
);
